import {
	accountingStandardSchema,
	balanceSheetId,
	componentExtractionStatusSchema,
	dataSourceSchema,
	fileId,
	financialExtractionId,
	financialPeriodId,
	financialPeriodPreparationMethodSchema,
	incomeStatementId,
	isoDateSchema,
	isoDateTimeSchema,
	periodTypeSchema,
} from "@inrev/common";
import * as z from "zod";
import { layeredLeafSchema, layeredTotalSchema } from "../schemas/layers";
import { defineTool } from "../tool-definition";

/**
 * `autoSource` is set only on lines the platform generated itself — the offset entries an auto
 * adjustment writes into a Dropped or equity bucket, tagged `auto_adjustment:<kind>:<source group>`.
 * Null means the line was read from the document. Without it a generated line is indistinguishable
 * from a reported one, since both carry a group label and a value.
 */
const lineItemSchema = z
	.strictObject({ label: z.string(), autoSource: z.string().nullable() })
	.extend(layeredLeafSchema.shape);

/**
 * Sections and groups are arrays carrying their own dotted `path` (`assets.current`) rather than
 * object keys, because the handler omits empty groups to save tokens and a fixed object shape
 * could not express that.
 */
const balanceSheetBody = z.strictObject({
	sections: z.array(
		z.strictObject({
			path: z.string(),
			label: z.string(),
			total: layeredTotalSchema,
			groups: z.array(
				z.strictObject({
					path: z.string(),
					label: z.string(),
					total: layeredTotalSchema,
					lineItems: z.array(lineItemSchema),
				}),
			),
		}),
	),
	totals: z.strictObject({
		assets: layeredTotalSchema,
		liabilities: layeredTotalSchema,
		equity: layeredTotalSchema,
	}),
});

const incomeStatementBody = z.strictObject({
	revenue: z.strictObject({
		total: layeredTotalSchema,
		lineItems: z.array(lineItemSchema),
	}),
	fields: z.array(
		z.strictObject({ key: z.string(), label: z.string() }).extend(layeredLeafSchema.shape),
	),
});

const statementBlock = <TId extends z.ZodType, TBody extends z.ZodType>(
	statementId: TId,
	body: TBody,
) =>
	z.strictObject({
		statementId,
		status: componentExtractionStatusSchema.nullable(),
		reviewedAt: isoDateTimeSchema.nullable(),
		supersededBy: statementId.nullable(),
		source: z.strictObject({
			fileId: fileId.nullable(),
			fileName: z.string().nullable(),
			extractionId: financialExtractionId.nullable(),
			dataSource: dataSourceSchema.nullable(),
		}),
		body,
	});

export const getFinancialStatement = defineTool({
	name: "get_financial_statement",
	description:
		"One period's balance sheet and income statement, line by line. Every figure carries three layers: `extracted` is the raw document read, `given` is the underwriter-corrected statement figure, and `allowed` is `given` plus adjustments. A total's adjustment is `allowed` minus `given`; a line's `adjustment` carries the formula and its `effects`, non-overlapping parts split by where the offset went — `line` changed that line only, `dropped` moved value out of the current section, `removed` wrote it off against equity. `reviewedAt` is when an underwriter signed the statement off; null means nobody has. A line with a non-null `autoSource` was generated by the platform as an adjustment offset, not read from the document. No profit subtotals are computed — the income statement returns `revenue` and each field on its own. `period.blocSize` is the bank line of credit size entered by hand, not read from a document; it can be missing or stale. `documentClassification` is a classifier model's read of the document, with its own `reasoning`, not an underwriter's finding: `multiPeriod` says whether the document presents more than one period, and `relatedParties` names the related parties it discloses.",
	label: "Financial statement",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	// The optional statement ids let the model read a superseded revision; omit them for the active one.
	input: z.strictObject({
		financialPeriodId,
		balanceSheetId: balanceSheetId.optional(),
		incomeStatementId: incomeStatementId.optional(),
	}),
	data: z.strictObject({
		period: z.strictObject({
			financialPeriodId,
			fiscalYear: z.number().int(),
			periodType: periodTypeSchema,
			periodStartDate: isoDateSchema.nullable(),
			periodEndDate: isoDateSchema,
			preparationMethod: financialPeriodPreparationMethodSchema,
			companyName: z.string().nullable(),
			blocSize: z.number().nullable(),
		}),
		balanceSheet: statementBlock(balanceSheetId, balanceSheetBody).nullable(),
		incomeStatement: statementBlock(incomeStatementId, incomeStatementBody).nullable(),
		documentClassification: z
			.strictObject({
				statementType: financialPeriodPreparationMethodSchema.nullable(),
				hasCpaReport: z.boolean().nullable(),
				cpaName: z.string().nullable(),
				accountingStandard: accountingStandardSchema.nullable(),
				isConsolidated: z.boolean().nullable(),
				priorPeriodRestatement: z.boolean().nullable(),
				goingConcernDoubt: z.boolean().nullable(),
				statementDate: isoDateSchema.nullable(),
				multiPeriod: z.boolean(),
				relatedParties: z.array(z.string()),
				reasoning: z.string().nullable(),
			})
			.nullable(),
	}),
});
